import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../ui/alert-dialog';
import { Plus, RefreshCcw, Pencil, Trash2, Eye, Upload, GripVertical, DownloadCloud, CheckCircle2, AlertTriangle, SkipForward, XCircle, Image as ImageIcon } from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import AuthImage from '../woty/AuthImage';
import ArtistProfileView from '../woty/ArtistProfileView';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EMPTY = {
  name: '', artworkTitle: '', bio: '', artistStatement: '',
  instagramUrl: '', facebookUrl: '', websiteUrl: '', location: '',
  displayOrder: 0, published: false, slug: '',
};

const adminImg = (id) => `${API}/contest/artists/${id}/image?variant=optimized`;

const ContestArtistsTab = () => {
  const [artists, setArtists] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null); // artist being edited or null (new)
  const [form, setForm] = useState(EMPTY);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [previewArtist, setPreviewArtist] = useState(null);
  const [importId, setImportId] = useState('');
  const [importingAll, setImportingAll] = useState(false);
  const [importReport, setImportReport] = useState(null);

  const authHeaders = () => ({ Authorization: `Bearer ${sessionStorage.getItem('adminToken')}` });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [a, s] = await Promise.all([
        axios.get(`${API}/contest/artists`, { headers: authHeaders() }),
        axios.get(`${API}/contest/submissions?limit=500`, { headers: authHeaders() }).catch(() => ({ data: { rows: [] } })),
      ]);
      setArtists(a.data.rows || []);
      setSubmissions(s.data.rows || []);
    } catch (err) {
      toast({ title: 'Failed to load artists', description: err.response?.data?.detail || 'Try again', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew = () => {
    setEditing(null);
    setForm(EMPTY);
    setImageFile(null);
    setImagePreview('');
    setDialogOpen(true);
  };

  const openEdit = (a) => {
    setEditing(a);
    setForm({
      name: a.name || '', artworkTitle: a.artworkTitle || '', bio: a.bio || '',
      artistStatement: a.artistStatement || '', instagramUrl: a.instagramUrl || '',
      facebookUrl: a.facebookUrl || '', websiteUrl: a.websiteUrl || '', location: a.location || '',
      displayOrder: a.displayOrder || 0, published: !!a.published, slug: a.slug || '',
    });
    setImageFile(null);
    setImagePreview('');
    setDialogOpen(true);
  };

  const onPickImage = (f) => {
    if (!f) return;
    if (!['image/jpeg', 'image/jpg', 'image/png', 'image/webp'].includes(f.type)) {
      toast({ title: 'Use a JPG, PNG or WEBP image', variant: 'destructive' });
      return;
    }
    setImageFile(f);
    setImagePreview(URL.createObjectURL(f));
  };

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const save = async () => {
    if (!form.name.trim()) {
      toast({ title: 'Artist name is required', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v === null ? '' : String(v)));
      if (imageFile) fd.append('image', imageFile, imageFile.name);
      if (editing) {
        await axios.put(`${API}/contest/artists/${editing.id}`, fd, { headers: authHeaders() });
        toast({ title: 'Artist updated' });
      } else {
        await axios.post(`${API}/contest/artists`, fd, { headers: authHeaders() });
        toast({ title: 'Artist created' });
      }
      setDialogOpen(false);
      await load();
    } catch (err) {
      toast({ title: 'Save failed', description: err.response?.data?.detail || 'Try again', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const togglePublish = async (a) => {
    try {
      await axios.patch(`${API}/contest/artists/${a.id}/publish`, { published: !a.published }, { headers: authHeaders() });
      setArtists((prev) => prev.map((x) => (x.id === a.id ? { ...x, published: !a.published } : x)));
    } catch (err) {
      toast({ title: 'Update failed', description: err.response?.data?.detail || 'Try again', variant: 'destructive' });
    }
  };

  const doDelete = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${API}/contest/artists/${deleteTarget.id}`, { headers: authHeaders() });
      toast({ title: 'Artist deleted' });
      setDeleteTarget(null);
      await load();
    } catch (err) {
      toast({ title: 'Delete failed', description: err.response?.data?.detail || 'Try again', variant: 'destructive' });
    }
  };

  const doImport = async () => {
    if (!importId) return;
    try {
      const { data } = await axios.post(`${API}/contest/artists/import/${importId}`, {}, { headers: authHeaders() });
      toast({ title: data.duplicate ? 'Already imported' : 'Entry imported as draft' });
      setImportId('');
      await load();
    } catch (err) {
      toast({ title: 'Import failed', description: err.response?.data?.detail || 'Try again', variant: 'destructive' });
    }
  };

  const doImportAll = async () => {
    setImportingAll(true);
    try {
      const { data } = await axios.post(`${API}/contest/artists/import-all`, {}, { headers: authHeaders() });
      setImportReport(data.report);
      await load();
      const r = data.report || {};
      toast({
        title: `Import complete — ${r.imported || 0} new`,
        description: `${r.found || 0} found · ${r.skipped || 0} already imported · ${r.attention || 0} need attention`,
      });
    } catch (err) {
      toast({ title: 'Import failed', description: err.response?.data?.detail || 'Try again', variant: 'destructive' });
    } finally {
      setImportingAll(false);
    }
  };

  const publishedCount = artists.filter((a) => a.published).length;

  return (
    <div className="space-y-4" data-testid="admin-artists-tab">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div className="grid grid-cols-2 gap-3 w-full sm:w-auto">
          <Stat label="Artists" value={artists.length} />
          <Stat label="Published" value={publishedCount} accent />
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="artists-refresh">
            <RefreshCcw className="w-4 h-4 mr-2" /> Refresh
          </Button>
          <Button size="sm" onClick={openNew} data-testid="artists-add">
            <Plus className="w-4 h-4 mr-2" /> Add Artist
          </Button>
        </div>
      </div>

      {/* Import from contest entries */}
      <div className="bg-[#FFFBEA] border border-[#FDB913]/40 rounded-lg p-3 space-y-3">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
          <div>
            <p className="text-sm font-semibold text-gray-800">Import contest submissions</p>
            <p className="text-xs text-gray-600">Pull every artist who submitted through the entry form into draft profiles — artwork and all. Safe to re-run; already-imported entries are skipped and your edits are never overwritten.</p>
          </div>
          <Button
            size="sm"
            onClick={doImportAll}
            disabled={importingAll}
            className="shrink-0 bg-[#722F37] hover:bg-[#5c252c] text-white"
            data-testid="artists-import-all-btn"
          >
            <DownloadCloud className="w-4 h-4 mr-2" />
            {importingAll ? 'Importing…' : 'Import all entries'}
          </Button>
        </div>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 border-t border-[#FDB913]/30 pt-3">
          <span className="text-xs text-gray-700 font-medium shrink-0">Or import a single entry:</span>
          <select
            value={importId}
            onChange={(e) => setImportId(e.target.value)}
            className="flex-1 min-w-0 px-3 py-2 rounded-md border border-gray-300 text-sm bg-white"
            data-testid="artists-import-select"
          >
            <option value="">Select an entry…</option>
            {submissions.map((s) => (
              <option key={s.id} value={s.id}>
                {s.firstName} {s.lastName} — {s.artworkTitle || 'Untitled'} ({s.entryId})
              </option>
            ))}
          </select>
          <Button size="sm" variant="outline" onClick={doImport} disabled={!importId} data-testid="artists-import-btn">
            <Upload className="w-4 h-4 mr-2" /> Import
          </Button>
        </div>
      </div>

      <div className="bg-white rounded-lg border shadow-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b">
            <tr className="text-left">
              <Th>Order</Th>
              <Th>Artwork</Th>
              <Th>Artist</Th>
              <Th>Title</Th>
              <Th>Slug</Th>
              <Th>Published</Th>
              <Th>Actions</Th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={7} className="text-center py-8 text-gray-400">Loading…</td></tr>}
            {!loading && artists.length === 0 && (
              <tr><td colSpan={7} className="text-center py-8 text-gray-400">
                No artists yet. Add one or import a contest entry above.
              </td></tr>
            )}
            {artists.map((a) => (
              <tr key={a.id} className="border-b last:border-b-0 hover:bg-gray-50">
                <Td><span className="inline-flex items-center gap-1 text-gray-400"><GripVertical className="w-3 h-3" />{a.displayOrder}</span></Td>
                <Td>
                  <div className="w-12 h-14 rounded overflow-hidden bg-gray-100">
                    {a.hasImage ? <AuthImage src={adminImg(a.id)} alt={a.name} className="w-full h-full object-cover" testId={`artist-thumb-${a.id}`} /> : <div className="w-full h-full" />}
                  </div>
                </Td>
                <Td className="font-medium text-gray-900">{a.name}{a.sourceSubmissionId && <span className="ml-1 text-[10px] text-gray-400">(entry)</span>}</Td>
                <Td>{a.artworkTitle || '—'}</Td>
                <Td className="font-mono text-xs text-gray-500">{a.slug}</Td>
                <Td>
                  <Switch checked={a.published} onCheckedChange={() => togglePublish(a)} data-testid={`artist-publish-${a.id}`} />
                </Td>
                <Td>
                  <div className="flex gap-1">
                    <IconBtn title="Preview" onClick={() => setPreviewArtist(a)} testId={`artist-preview-${a.id}`}><Eye className="w-4 h-4" /></IconBtn>
                    <IconBtn title="Replace artwork" onClick={() => openEdit(a)} testId={`artist-replace-art-${a.id}`}><ImageIcon className="w-4 h-4 text-[#722F37]" /></IconBtn>
                    <IconBtn title="Edit" onClick={() => openEdit(a)} testId={`artist-edit-${a.id}`}><Pencil className="w-4 h-4" /></IconBtn>
                    <IconBtn title="Delete" onClick={() => setDeleteTarget(a)} testId={`artist-delete-${a.id}`}><Trash2 className="w-4 h-4 text-red-600" /></IconBtn>
                  </div>
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Add / Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="artist-form-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit artist' : 'Add artist'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Artist name *"><Input value={form.name} onChange={(e) => set('name', e.target.value)} data-testid="artist-field-name" /></Field>
              <Field label="Artwork title"><Input value={form.artworkTitle} onChange={(e) => set('artworkTitle', e.target.value)} data-testid="artist-field-title" /></Field>
            </div>
            <Field label="Biography"><Textarea rows={3} value={form.bio} onChange={(e) => set('bio', e.target.value)} data-testid="artist-field-bio" /></Field>
            <Field label="Artist statement / about the design"><Textarea rows={3} value={form.artistStatement} onChange={(e) => set('artistStatement', e.target.value)} data-testid="artist-field-statement" /></Field>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Field label="Instagram URL"><Input value={form.instagramUrl} onChange={(e) => set('instagramUrl', e.target.value)} data-testid="artist-field-instagram" /></Field>
              <Field label="Facebook URL"><Input value={form.facebookUrl} onChange={(e) => set('facebookUrl', e.target.value)} data-testid="artist-field-facebook" /></Field>
              <Field label="Website URL"><Input value={form.websiteUrl} onChange={(e) => set('websiteUrl', e.target.value)} data-testid="artist-field-website" /></Field>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Field label="Public location (optional)"><Input value={form.location} onChange={(e) => set('location', e.target.value)} placeholder="Fenelon Falls, ON" data-testid="artist-field-location" /></Field>
              <Field label="Display order"><Input type="number" value={form.displayOrder} onChange={(e) => set('displayOrder', e.target.value)} data-testid="artist-field-order" /></Field>
              <Field label="URL slug (auto if blank)"><Input value={form.slug} onChange={(e) => set('slug', e.target.value)} placeholder="auto-from-name" data-testid="artist-field-slug" /></Field>
            </div>

            <Field label="Artwork image (JPG / PNG / WEBP)">
              <div className="flex items-center gap-4">
                <div className="w-20 h-24 rounded overflow-hidden bg-gray-100 shrink-0">
                  {imagePreview ? (
                    <img src={imagePreview} alt="preview" className="w-full h-full object-cover" />
                  ) : editing?.hasImage ? (
                    <AuthImage src={adminImg(editing.id)} alt="current" className="w-full h-full object-cover" />
                  ) : <div className="w-full h-full flex items-center justify-center text-[10px] text-gray-400">None</div>}
                </div>
                <label className="inline-flex items-center gap-2 px-4 py-2 rounded-md border cursor-pointer text-sm hover:bg-gray-50" style={{ borderColor: '#e4d3a8' }}>
                  <Upload className="w-4 h-4" /> {editing?.hasImage || imagePreview ? 'Replace image' : 'Upload image'}
                  <input type="file" accept=".jpg,.jpeg,.png,.webp" className="sr-only" onChange={(e) => onPickImage(e.target.files?.[0])} data-testid="artist-field-image" />
                </label>
              </div>
            </Field>

            <div className="flex items-center gap-3 pt-2">
              <Switch checked={form.published} onCheckedChange={(v) => set('published', v)} data-testid="artist-field-published" />
              <span className="text-sm text-gray-700">Published (visible in the public gallery)</span>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="artist-save">{saving ? 'Saving…' : 'Save artist'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview dialog */}
      <Dialog open={!!previewArtist} onOpenChange={(o) => !o && setPreviewArtist(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="artist-preview-dialog">
          <DialogHeader><DialogTitle>Profile preview</DialogTitle></DialogHeader>
          {previewArtist && (
            <ArtistProfileView
              artist={previewArtist}
              imageElement={previewArtist.hasImage ? <AuthImage src={adminImg(previewArtist.id)} alt={previewArtist.name} className="w-full h-full object-cover" /> : null}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Import report */}
      <Dialog open={!!importReport} onOpenChange={(o) => !o && setImportReport(null)}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="import-report-dialog">
          <DialogHeader>
            <DialogTitle>Import report</DialogTitle>
            <DialogDescription>
              Results of pulling contest submissions into the voting competition.
            </DialogDescription>
          </DialogHeader>
          {importReport && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                <ReportStat label="Found" value={importReport.found} />
                <ReportStat label="Imported" value={importReport.imported} tone="green" />
                <ReportStat label="Skipped" value={importReport.skipped} tone="gray" />
                <ReportStat label="Attention" value={importReport.attention} tone="amber" />
                <ReportStat label="Failed" value={importReport.failed} tone="red" />
              </div>
              {importReport.found === 0 && (
                <p className="text-sm text-gray-600 bg-gray-50 border rounded-md p-3" data-testid="import-report-empty">
                  No contest submissions were found in this database. If artists submitted on your live site,
                  run this import on the deployed (production) app where those records live.
                </p>
              )}
              <div className="space-y-1.5">
                {(importReport.items || []).map((it, i) => (
                  <div key={i} className="flex items-start gap-2 text-sm border-b last:border-b-0 py-1.5">
                    <ReportIcon status={it.status} />
                    <div className="min-w-0 flex-1">
                      <span className="font-medium text-gray-900">{it.name || 'Unknown artist'}</span>
                      {it.entryId && <span className="ml-1 text-xs text-gray-400">{it.entryId}</span>}
                      {it.reason && <span className="ml-1 text-xs text-gray-500">· {reasonLabel(it.reason)}</span>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setImportReport(null)} data-testid="import-report-close">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="artist-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this artist?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes <strong>{deleteTarget?.name}</strong> and their profile. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-red-600 hover:bg-red-700" data-testid="artist-delete-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

const Field = ({ label, children }) => (
  <div>
    <Label className="text-xs text-gray-600 mb-1 block">{label}</Label>
    {children}
  </div>
);

const Stat = ({ label, value, accent }) => (
  <div className={`rounded-lg border px-3 py-2 ${accent ? 'bg-[#FFFBEA] border-[#FDB913]/40' : 'bg-white'}`}>
    <div className="text-xs text-gray-500 uppercase tracking-wide">{label}</div>
    <div className="text-2xl font-bold text-gray-900">{value}</div>
  </div>
);

const Th = ({ children }) => (
  <th className="px-4 py-2.5 text-xs font-semibold text-gray-600 uppercase tracking-wide whitespace-nowrap">{children}</th>
);
const Td = ({ children, className = '' }) => <td className={`px-4 py-2.5 ${className}`}>{children}</td>;
const IconBtn = ({ children, title, onClick, testId }) => (
  <button title={title} onClick={onClick} data-testid={testId} className="p-1.5 rounded hover:bg-gray-100 border border-transparent hover:border-gray-200">
    {children}
  </button>
);

const REPORT_TONES = {
  green: 'bg-green-50 border-green-200 text-green-700',
  amber: 'bg-amber-50 border-amber-200 text-amber-700',
  red: 'bg-red-50 border-red-200 text-red-700',
  gray: 'bg-gray-50 border-gray-200 text-gray-600',
  default: 'bg-white border-gray-200 text-gray-900',
};
const ReportStat = ({ label, value, tone = 'default' }) => (
  <div className={`rounded-lg border px-2 py-2 text-center ${REPORT_TONES[tone]}`} data-testid={`import-report-${label.toLowerCase()}`}>
    <div className="text-xl font-bold">{value ?? 0}</div>
    <div className="text-[10px] uppercase tracking-wide opacity-80">{label}</div>
  </div>
);
const ReportIcon = ({ status }) => {
  if (status === 'imported') return <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 shrink-0" />;
  if (status === 'attention') return <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" />;
  if (status === 'skipped') return <SkipForward className="w-4 h-4 text-gray-400 mt-0.5 shrink-0" />;
  return <XCircle className="w-4 h-4 text-red-500 mt-0.5 shrink-0" />;
};
const REASONS = {
  already_imported: 'already imported',
  no_artwork: 'no artwork file — add an image',
  non_image_artwork: 'artwork is a PDF — add a display image',
  artwork_fetch_failed: 'could not load artwork — add an image',
  missing_name: 'no name on entry',
  missing_name_and_email: 'no name or email — cannot import',
  import_error: 'unexpected error',
};
const reasonLabel = (r) => REASONS[r] || r;

export default ContestArtistsTab;
